import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles, TrendingUp, Wrench, ShieldCheck, Palette, Zap, Plug,
  X, RefreshCw, ArrowRight, Calendar,
} from "lucide-react";
import { useApp, API } from "../AppContext";
import { CURRENT_MODULE_CODE, CURRENT_MODULE_NAME } from "../config/progere-modules";
import ProgereTwoToneTitle from "./ProgereTwoToneTitle";

const STORAGE_KEY = `progere_release_seen_${CURRENT_MODULE_CODE}`;

/** Icône + couleur par catégorie */
export const CATEGORY_META = {
  new:         { Icon: Sparkles,   color: "var(--pg-gold)",    labelFr: "NOUVEAU",       labelEn: "NEW" },
  improvement: { Icon: TrendingUp, color: "var(--pg-info)",    labelFr: "AMÉLIORÉ",      labelEn: "IMPROVED" },
  fix:         { Icon: Wrench,     color: "var(--pg-success)", labelFr: "CORRIGÉ",       labelEn: "FIXED" },
  security:    { Icon: ShieldCheck, color: "var(--pg-danger)", labelFr: "SÉCURITÉ",      labelEn: "SECURITY" },
  design:      { Icon: Palette,    color: "var(--pg-gold)",    labelFr: "DESIGN",        labelEn: "DESIGN" },
  performance: { Icon: Zap,        color: "var(--pg-info)",    labelFr: "PERFORMANCE",   labelEn: "PERFORMANCE" },
  integration: { Icon: Plug,       color: "var(--pg-success)", labelFr: "INTÉGRATION",   labelEn: "INTEGRATION" },
  legal:       { Icon: ShieldCheck, color: "var(--pg-gold)",   labelFr: "LÉGAL",         labelEn: "LEGAL" },
  mobile:      { Icon: Zap,        color: "var(--pg-info)",    labelFr: "MOBILE",        labelEn: "MOBILE" },
  automation:  { Icon: Zap,        color: "var(--pg-info)",    labelFr: "AUTOMATISATION", labelEn: "AUTOMATION" },
};

function pickList(release, keyFr, keyEn, isFr) {
  const primary = isFr ? release?.[keyFr] : release?.[keyEn];
  if (Array.isArray(primary) && primary.length > 0) return primary;
  const fallback = isFr ? release?.[keyEn] : release?.[keyFr];
  return Array.isArray(fallback) ? fallback : [];
}

const ChangeBlock = ({ items, category, isFr }) => {
  if (!items || items.length === 0) return null;
  const meta = CATEGORY_META[category] || CATEGORY_META.new;
  const Icon = meta.Icon;
  return (
    <div className="mb-4" data-testid={`release-block-${category}`}>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded font-black text-[10px] uppercase"
          style={{
            background: `color-mix(in srgb, ${meta.color} 16%, transparent)`,
            color: meta.color,
            border: `1px solid color-mix(in srgb, ${meta.color} 40%, transparent)`,
          }}
        >
          <Icon size={11} />
          {isFr ? meta.labelFr : meta.labelEn}
        </span>
      </div>
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div
            key={i}
            className="progere-release-item flex items-start gap-2 px-3 py-2 rounded-lg text-sm"
            style={{ background: "var(--pg-bg-tertiary)", border: "1px solid var(--pg-border)", color: "var(--pg-text-primary)" }}
          >
            <Icon size={14} style={{ color: meta.color, flexShrink: 0, marginTop: 2 }} />
            <span>{it}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * ProgereReleasePopup — Fenêtre affichée à l'ouverture de l'app
 * lorsqu'une nouvelle version publiée n'a pas encore été consultée
 * par l'utilisateur connecté.
 */
export default function ProgereReleasePopup() {
  const { lang, token } = useApp();
  const isFr = lang === "fr";
  const [release, setRelease] = useState(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios.get(`${API}/releases/unseen/${CURRENT_MODULE_CODE}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      if (cancelled) return;
      if (data?.has_update && data?.release) {
        // dédupe locale en plus du serveur (safeguard)
        try {
          const seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
          if (seen.includes(data.release.id)) return;
        } catch {
          // ignore
        }
        setRelease(data.release);
        setOpen(true);
        // Marquer comme first-seen
        axios.post(`${API}/releases/mark-seen`, { release_id: data.release.id },
          { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  if (!open || !release) return null;

  const T = isFr ? {
    tagline: "NOUVELLE VERSION",
    version: "Version",
    seeAll: "Voir toutes les nouveautés",
    ackClose: "Continuer",
    discover: "Découvrir les nouveautés",
    mandatory: "MISE À JOUR REQUISE",
    mandatoryHint: "Une mise à jour importante est disponible. Actualisez pour continuer.",
    update: "Mettre à jour maintenant",
    published: "Publié le",
  } : {
    tagline: "NEW VERSION",
    version: "Version",
    seeAll: "See all release notes",
    ackClose: "Continue",
    discover: "Discover what's new",
    mandatory: "UPDATE REQUIRED",
    mandatoryHint: "An important update is available. Refresh to continue.",
    update: "Update now",
    published: "Published on",
  };

  const acknowledge = async ({ dismiss = true }) => {
    try {
      await axios.post(`${API}/releases/acknowledge`, {
        release_id: release.id, viewed_full_notes: expanded,
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* ignore */ }
    try {
      const seen = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      if (!seen.includes(release.id)) seen.push(release.id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
    } catch { /* ignore */ }
    if (dismiss) setOpen(false);
  };

  const highlights = pickList(release, "highlights_fr", "highlights_en", isFr);
  const improvements = pickList(release, "improvements_fr", "improvements_en", isFr);
  const fixes = pickList(release, "fixes_fr", "fixes_en", isFr);
  const security = pickList(release, "security_updates_fr", "security_updates_en", isFr);
  const title = (isFr ? release.title_fr : release.title_en) || release.title_fr || release.title_en;
  const publishedAt = release.published_at ? new Date(release.published_at).toLocaleDateString(isFr ? "fr-CA" : "en-CA", { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4" data-testid="release-popup">
      <div
        className="absolute inset-0"
        style={{ background: "var(--pg-bg-overlay)", backdropFilter: "blur(6px)" }}
        onClick={() => !release.mandatory_update && acknowledge({ dismiss: true })}
      />
      <div
        className="progere-release-popup relative"
        style={{
          width: "min(620px, calc(100vw - 32px))",
          maxHeight: "min(760px, calc(100vh - 48px))",
          overflowY: "auto",
          color: "var(--pg-text-primary)",
          background: "var(--pg-bg-card)",
          border: "1px solid color-mix(in srgb, var(--pg-gold) 58%, var(--pg-border))",
          borderRadius: 14,
          boxShadow: "0 28px 80px rgba(0,0,0,0.55), 0 0 30px rgba(242,188,46,0.10)",
          padding: 28,
        }}
      >
        {/* Close */}
        {!release.mandatory_update && (
          <button
            onClick={() => acknowledge({ dismiss: true })}
            data-testid="release-popup-close"
            aria-label="close"
            className="absolute top-4 right-4 p-2 rounded-full"
            style={{ background: "var(--pg-bg-tertiary)", border: "1px solid var(--pg-border)" }}
          >
            <X size={14} style={{ color: "var(--pg-text-muted)" }} />
          </button>
        )}

        {/* Header */}
        <div className="mb-1 text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--pg-gold)" }}>
          {release.mandatory_update ? T.mandatory : T.tagline}
        </div>
        <ProgereTwoToneTitle firstPart="PROGÈRE" secondPart={CURRENT_MODULE_NAME} size="text-2xl sm:text-3xl" />
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span
            className="progere-release-version inline-flex items-center gap-1 px-2 py-1 rounded font-black text-[11px]"
            style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
            data-testid="release-popup-version"
          >
            {T.version} {release.version}
          </span>
          {publishedAt && (
            <span className="text-[11px] inline-flex items-center gap-1" style={{ color: "var(--pg-text-muted)" }}>
              <Calendar size={10} /> {T.published} {publishedAt}
            </span>
          )}
        </div>

        {title && (
          <p className="mt-4 text-base" style={{ color: "var(--pg-text-secondary)" }}>{title}</p>
        )}

        {release.mandatory_update && (
          <div
            className="mt-4 rounded-xl p-4 flex items-start gap-2 text-sm"
            style={{
              background: "color-mix(in srgb, var(--pg-danger) 10%, transparent)",
              border: "1px solid var(--pg-danger)",
              color: "var(--pg-text-primary)",
            }}
          >
            <ShieldCheck size={16} style={{ color: "var(--pg-danger)", flexShrink: 0 }} />
            <span>{T.mandatoryHint}</span>
          </div>
        )}

        {/* Change blocks */}
        <div className="mt-5">
          <ChangeBlock items={highlights} category="new" isFr={isFr} />
          <ChangeBlock items={improvements} category="improvement" isFr={isFr} />
          <ChangeBlock items={fixes} category="fix" isFr={isFr} />
          <ChangeBlock items={security} category="security" isFr={isFr} />
        </div>

        {/* Footer */}
        <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {release.mandatory_update ? (
            <button
              data-testid="release-popup-update"
              onClick={() => { acknowledge({ dismiss: true }); window.location.reload(); }}
              className="flex-1 text-sm font-bold px-4 py-2.5 rounded-full inline-flex items-center justify-center gap-2"
              style={{ background: "var(--pg-red)", color: "#fff" }}
            >
              <RefreshCw size={14} /> {T.update}
            </button>
          ) : (
            <>
              <Link
                to="/nouveautes"
                onClick={() => setExpanded(true)}
                data-testid="release-popup-seeall"
                className="text-sm font-semibold px-4 py-2.5 rounded-full inline-flex items-center justify-center gap-1.5"
                style={{ background: "transparent", color: "var(--pg-text-secondary)", border: "1px solid var(--pg-border-strong)" }}
              >
                {T.seeAll} <ArrowRight size={12} />
              </Link>
              <button
                data-testid="release-popup-ack"
                onClick={() => acknowledge({ dismiss: true })}
                className="flex-1 text-sm font-bold px-4 py-2.5 rounded-full"
                style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
              >
                {T.ackClose}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
